import { CSSObject, Theme } from '@mui/material'
import { spacing } from '../tokens/spacing'
import { insetBorder } from './insetBorder'

export type IconButtonVariant = 'contained' | 'ghost'
export type IconButtonColour = 'primary' | 'secondary' | 'error'
export type IconButtonSize = 'small' | 'medium' | 'large'

export const ICON_FONT_SIZE = '1.25rem'

export const iconButtonIconStyle: CSSObject = {
  '& .MuiSvgIcon-root': {
    fontSize: ICON_FONT_SIZE,
  },
}

const paddingFactors: Record<IconButtonSize, number> = {
  large: 2.5,
  medium: 2,
  small: 1,
}

const boxSize = (size: IconButtonSize) => `calc(${ICON_FONT_SIZE} + ${spacing(paddingFactors[size])} * 2)`

/** The rendered box of each size — the icon plus its own padding on both sides. Layout that has to
 * line up with an IconButton, or hold its place while it is absent, reserves this. */
export const iconButtonBoxSizes: Record<IconButtonSize, string> = {
  large: boxSize('large'),
  medium: boxSize('medium'),
  small: boxSize('small'),
}

// Nucleo icons (the `*OMIcon` set) render a plain <svg>, not MUI's SvgIcon, so they don't carry
// MuiSvgIcon-root — this selector catches that case so the padding tuned for MUI's icons doesn't
// leave a Nucleo icon looking lost in the middle.
const rawSvgChildSelector = '&:has(> svg:not(.MuiSvgIcon-root))'
// A Nucleo icon's own inset padding reads as ~1px slimmer than a MuiSvgIcon at the same nominal
// size, so the raw-svg case gets an extra 1px on top of the button's own themed padding — expressed
// as that sum rather than a single number so it doesn't read as an arbitrary non-standard value.
const paddingStyle = (theme: Theme, size: IconButtonSize): CSSObject => ({
  padding: theme.spacing(paddingFactors[size]),
  [rawSvgChildSelector]: {
    padding: `calc(${theme.spacing(paddingFactors[size])} + 1px)`,
  },
})

export const iconButtonSizeStyles = (theme: Theme): Record<IconButtonSize, CSSObject> => ({
  large: {
    borderRadius: theme.shape['radius-lg'],
    ...paddingStyle(theme, 'large'),
  },
  medium: {
    borderRadius: theme.shape['radius-lg'],
    ...paddingStyle(theme, 'medium'),
  },
  small: {
    borderRadius: theme.shape['radius-md'],
    ...paddingStyle(theme, 'small'),
  },
})

type IconButtonColourStyle = {
  base: CSSObject
  hover: CSSObject
  // Held while the button can't respond — disabled, or loading where the component has an indicator
  disabled: CSSObject
}

export const iconButtonColourStyles = (
  theme: Theme
): Record<IconButtonVariant, Record<IconButtonColour, IconButtonColourStyle>> => ({
  contained: {
    primary: {
      base: { backgroundColor: theme.palette.bg.brand.default, color: theme.palette.text.brand.onBrand },
      hover: { backgroundColor: theme.palette.bg.brand.hover },
      disabled: { backgroundColor: theme.palette.bg.brand.default, color: theme.palette.text.brand.onBrand },
    },
    secondary: {
      base: {
        backgroundColor: theme.palette.bg.surface.elevated,
        color: theme.palette.text.muted,
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
      hover: {
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.strong }),
          theme.customShadows.buttonInner,
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
      disabled: {
        backgroundColor: theme.palette.bg.surface.elevated,
        color: theme.palette.text.default,
        boxShadow: [
          insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.subtle }),
          theme.customShadows.buttonInner,
          theme.customShadows.buttonDrop,
        ].join(', '),
      },
    },
    error: {
      base: { backgroundColor: theme.palette.bg.danger.default, color: theme.palette.text.danger.onDanger },
      hover: { backgroundColor: theme.palette.bg.danger.hover },
      disabled: { backgroundColor: theme.palette.bg.danger.default, color: theme.palette.text.danger.onDanger },
    },
  },
  ghost: {
    primary: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.brand.default },
      hover: { backgroundColor: theme.palette.bg.brand.muted, color: theme.palette.text.brand.onBrandMuted },
      disabled: { color: theme.palette.text.brand.default },
    },
    secondary: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.muted },
      hover: { backgroundColor: theme.palette.bg.interactive.hover, color: theme.palette.text.default },
      disabled: { color: theme.palette.text.default },
    },
    error: {
      base: { backgroundColor: theme.palette.bg.invisible, color: theme.palette.text.danger.default },
      hover: { backgroundColor: theme.palette.bg.danger.muted, color: theme.palette.text.danger.onDangerMuted },
      disabled: { color: theme.palette.text.danger.onDangerMuted },
    },
  },
})
